/**
 * Admin Markets — Iter-191
 *
 * CRUD page for managing the metro markets catalog plus the
 * "market not listed" review queue from talent.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { ArrowLeft, MapPin, Plus, Pencil, Users, Inbox, CheckCircle2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

export default function AdminMarkets() {
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('catalog'); // 'catalog' | 'queue'
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveItem, setResolveItem] = useState(null);
  const [resolveMarketId, setResolveMarketId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [m, q] = await Promise.all([
        api.get('/admin/markets', { params: { include_inactive: true } }),
        api.get('/admin/markets/review-queue'),
      ]);
      setMarkets(Array.isArray(m.data) ? m.data : []);
      setQueue(Array.isArray(q.data) ? q.data : []);
    } catch (e) {
      toast.error('Failed to load markets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing({
      id: null,
      slug: '',
      metro_name: '',
      states_covered: '',
      cities_covered: '',
      is_active: true,
      sort_priority: 100,
    });
    setEditorOpen(true);
  };

  const openEdit = (m) => {
    setEditing({
      id: m.id,
      slug: m.slug,
      metro_name: m.metro_name,
      states_covered: (m.states_covered || []).join(', '),
      cities_covered: (m.cities_covered || []).join(', '),
      is_active: m.is_active,
      sort_priority: m.sort_priority,
    });
    setEditorOpen(true);
  };

  const saveMarket = async () => {
    if (!editing.metro_name || editing.metro_name.length < 2) {
      toast.error('Metro name is required');
      return;
    }
    const states = editing.states_covered.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
    const cities = editing.cities_covered.split(',').map((s) => s.trim()).filter(Boolean);
    try {
      if (editing.id) {
        await api.patch(`/admin/markets/${editing.id}`, {
          metro_name: editing.metro_name,
          states_covered: states,
          cities_covered: cities,
          is_active: editing.is_active,
          sort_priority: parseInt(editing.sort_priority) || 100,
        });
        toast.success('Market updated');
      } else {
        const slug = editing.slug || slugify(editing.metro_name);
        await api.post('/admin/markets', {
          slug,
          metro_name: editing.metro_name,
          states_covered: states,
          cities_covered: cities,
          sort_priority: parseInt(editing.sort_priority) || 100,
        });
        toast.success('Market created');
      }
      setEditorOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save market');
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/admin/markets/${m.id}`, { is_active: !m.is_active });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const openResolve = (item) => {
    setResolveItem(item);
    setResolveMarketId('');
    setResolveOpen(true);
  };

  const submitResolve = async (dismiss = false) => {
    try {
      await api.post(`/admin/markets/review-queue/${resolveItem.id}/resolve`, {
        resolved_market_id: dismiss ? null : resolveMarketId || null,
        dismiss,
      });
      toast.success(dismiss ? 'Request dismissed' : 'Request resolved');
      setResolveOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <MapPin className="w-5 h-5 text-cyan-500" />
          <h1 className="text-base sm:text-lg font-semibold flex-1">Markets</h1>
          <Button onClick={openCreate} className="bg-cyan-600 hover:bg-cyan-700 text-white h-9" data-testid="create-market-btn">
            <Plus className="w-4 h-4 mr-1" /> New Market
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        <div className="flex gap-2 border-b">
          <button
            type="button"
            onClick={() => setTab('catalog')}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === 'catalog' ? 'border-cyan-600 text-cyan-700' : 'border-transparent text-gray-500'}`}
            data-testid="tab-catalog"
          >
            Catalog ({markets.length})
          </button>
          <button
            type="button"
            onClick={() => setTab('queue')}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === 'queue' ? 'border-cyan-600 text-cyan-700' : 'border-transparent text-gray-500'}`}
            data-testid="tab-queue"
          >
            <Inbox className="w-3.5 h-3.5 inline-block mr-1" />
            Review Queue ({queue.length})
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-12 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : tab === 'catalog' ? (
          <div className="space-y-2">
            {markets.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-sm text-gray-500">No markets yet.</CardContent></Card>
            ) : markets.map((m) => (
              <Card key={m.id} data-testid={`market-row-${m.slug}`}>
                <CardContent className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-sm text-gray-900">{m.metro_name}</h3>
                      {!m.is_active && <Badge className="bg-gray-200 text-gray-700 text-[10px]">Inactive</Badge>}
                      <Badge className="bg-cyan-50 text-cyan-700 border border-cyan-200 text-[10px]">
                        <Users className="w-3 h-3 mr-1" /> {m.talent_count} talent
                      </Badge>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                      {m.slug} · {(m.states_covered || []).join(', ') || 'no states'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Switch
                      checked={m.is_active}
                      onCheckedChange={() => toggleActive(m)}
                      data-testid={`market-toggle-${m.slug}`}
                    />
                    <Button size="sm" variant="outline" onClick={() => openEdit(m)} className="h-8 text-xs" data-testid={`market-edit-${m.slug}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {queue.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-sm text-gray-500">
                No pending market requests.
              </CardContent></Card>
            ) : queue.map((q) => (
              <Card key={q.id} data-testid={`queue-row-${q.id}`}>
                <CardContent className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{q.talent_name}</div>
                    <div className="text-[11px] text-gray-500 truncate">
                      Wants: {q.requested_city || '—'}, {q.requested_state || '—'}
                      {q.note && <> · "{q.note}"</>}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => openResolve(q)} className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 text-xs" data-testid={`queue-resolve-${q.id}`}>
                    Resolve
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-md" data-testid="market-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Market' : 'New Market'}</DialogTitle>
            <DialogDescription className="text-xs">
              Markets are the metro regions talent select and admins target.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Metro Name</Label>
                <Input
                  value={editing.metro_name}
                  onChange={(e) => setEditing({ ...editing, metro_name: e.target.value })}
                  placeholder="e.g. Atlanta, GA"
                  className="h-9 text-sm"
                  data-testid="editor-metro-name"
                />
              </div>
              {!editing.id && (
                <div>
                  <Label className="text-xs">Slug (auto-generated if blank)</Label>
                  <Input
                    value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    placeholder={slugify(editing.metro_name)}
                    className="h-9 text-sm"
                    data-testid="editor-slug"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs">States Covered (comma-separated 2-letter codes)</Label>
                <Input
                  value={editing.states_covered}
                  onChange={(e) => setEditing({ ...editing, states_covered: e.target.value })}
                  placeholder="GA, AL"
                  className="h-9 text-sm"
                  data-testid="editor-states"
                />
              </div>
              <div>
                <Label className="text-xs">Cities Covered (comma-separated, lowercased on save)</Label>
                <Input
                  value={editing.cities_covered}
                  onChange={(e) => setEditing({ ...editing, cities_covered: e.target.value })}
                  placeholder="atlanta, alpharetta"
                  className="h-9 text-sm"
                  data-testid="editor-cities"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Sort Priority</Label>
                  <Input
                    type="number"
                    value={editing.sort_priority}
                    onChange={(e) => setEditing({ ...editing, sort_priority: e.target.value })}
                    className="h-9 text-sm"
                    data-testid="editor-sort"
                  />
                </div>
                {editing.id && (
                  <div className="flex flex-col">
                    <Label className="text-xs">Active</Label>
                    <Switch
                      checked={editing.is_active}
                      onCheckedChange={(v) => setEditing({ ...editing, is_active: v })}
                      data-testid="editor-active"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveMarket} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="editor-save">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve dialog */}
      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent className="max-w-md" data-testid="resolve-dialog">
          <DialogHeader>
            <DialogTitle>Resolve Market Request</DialogTitle>
            <DialogDescription className="text-xs">
              {resolveItem && (<>
                Add <strong>{resolveItem.talent_name}</strong> to a market, or dismiss.
              </>)}
            </DialogDescription>
          </DialogHeader>
          {resolveItem && (
            <div className="space-y-3">
              <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
                Requested: {resolveItem.requested_city || '—'}, {resolveItem.requested_state || '—'}
                {resolveItem.note && <div className="mt-1 italic">"{resolveItem.note}"</div>}
              </div>
              <div>
                <Label className="text-xs">Add talent to market</Label>
                <Select value={resolveMarketId} onValueChange={setResolveMarketId}>
                  <SelectTrigger className="h-9 text-sm" data-testid="resolve-market-select">
                    <SelectValue placeholder="Pick a market…" />
                  </SelectTrigger>
                  <SelectContent>
                    {markets.filter((m) => m.is_active).map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.metro_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter className="flex-row gap-2 justify-between">
            <Button variant="ghost" onClick={() => submitResolve(true)} className="text-xs" data-testid="resolve-dismiss">
              <X className="w-3.5 h-3.5 mr-1" /> Dismiss
            </Button>
            <Button
              onClick={() => submitResolve(false)}
              disabled={!resolveMarketId}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="resolve-confirm"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
